import React, { memo, useCallback, useMemo, useState } from "react";
import useAuth from "../../Hooks/useAuth";
import { useLazySearchFirmsForDropdownQuery } from "../../store/storageRoles/firmApi";
import { CustomDropdown } from "../atoms";
import { getFirmTypeStyle } from "../../utils/firmTypeStyles";
import { parentKindLabel } from "../../utils/firmParent";

const NONE = { id: "none", value: "none", label: "Üst firma yok", kind: null };

const toOption = (kind, f) => ({
  id: `${kind}:${f._id}`,
  value: `${kind}:${f._id}`,
  label: f.firmName,
  kind,
  firmId: f._id,
  firmName: f.firmName,
  firmType: f.firmType || (kind === "dealer" ? "dealer" : null),
});

/**
 * Molekül: tek "Üst Firma" seçici (#373). Tek aramada ana firma adayları
 * (firmayla aynı tip) ve —müşteri firmada— bayiler, tip rozetiyle listelenir.
 * Bayi ya da ana firma seçilir; ikisi birlikte olmaz (sunucu kilidi v1.1).
 *
 * Firma detayı → Bilgi sekmesi ve firma oluşturma sihirbazı ortak kullanır.
 *
 * @param {"customer"|"dealer"} firmType düzenlenen firmanın tipi
 * @param {string} [excludeFirmId] firmanın kendisi (listede çıkmasın)
 * @param {{kind, id, firmName, firmType}|null} value
 * @param {(parent|null) => void} onChange
 * @param {boolean} [allowMain=true] ana firma seçilebilir (firms.editMainFirm)
 * @param {boolean} [allowDealer=true] bayi seçilebilir (yalnız müşteri, firms.editDealerRelation)
 */
function ParentFirmPicker({
  firmType,
  excludeFirmId,
  value,
  onChange,
  allowMain = true,
  allowDealer = true,
  disabled = false,
  label,
  compact = false,
}) {
  const { auth } = useAuth();
  const [searchFirms] = useLazySearchFirmsForDropdownQuery();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dealerAllowed = allowDealer && firmType === "customer";

  const handleSearch = useCallback(
    async (term) => {
      setLoading(true);
      const base = { token: auth.accessToken, search: term, excludeFirmId };
      const [mains, dealers] = await Promise.all([
        allowMain
          ? searchFirms({ ...base, firmType }).unwrap().catch(() => null)
          : Promise.resolve(null),
        dealerAllowed
          ? searchFirms({ ...base, firmType: "dealer" }).unwrap().catch(() => null)
          : Promise.resolve(null),
      ]);
      const list = [
        ...(mains?.data?.firms || []).filter((f) => f._id !== excludeFirmId).map((f) => toOption("main", f)),
        ...(dealers?.data?.firms || []).filter((f) => f._id !== excludeFirmId).map((f) => toOption("dealer", f)),
      ];
      setOptions(list);
      setLoading(false);
    },
    [auth.accessToken, excludeFirmId, firmType, allowMain, dealerAllowed, searchFirms]
  );

  // Seçili değer listede olmasa da kutuda görünsün (liste yalnız aramada dolar)
  const selected = useMemo(() => {
    if (!value) return NONE;
    const key = `${value.kind}:${value.id}`;
    return (
      options.find((o) => o.value === key) || {
        id: key,
        value: key,
        label: value.firmName,
        kind: value.kind,
        firmId: value.id,
        firmName: value.firmName,
        firmType: value.firmType,
      }
    );
  }, [value, options]);

  const renderTagged = (opt) => {
    if (!opt?.kind) return <span className="text-gray-500">{opt?.label || NONE.label}</span>;
    const style = getFirmTypeStyle(opt.kind === "dealer" ? "dealer" : opt.firmType || firmType);
    return (
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate">{opt.label}</span>
        <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-SemiBold ${style.badge}`}>
          {parentKindLabel(opt, firmType)}
        </span>
      </span>
    );
  };

  return (
    <CustomDropdown
      label={label}
      compact={compact}
      value={selected}
      options={[NONE, ...options]}
      onChange={(opt) =>
        onChange(
          opt?.kind
            ? { kind: opt.kind, id: opt.firmId, firmName: opt.firmName, firmType: opt.firmType }
            : null
        )
      }
      onSearch={handleSearch}
      searchDebounce={400}
      loading={loading}
      disabled={disabled}
      placeholder="Üst firma seçin"
      searchPlaceholder={dealerAllowed && allowMain ? "Ana firma ya da bayi ara..." : "Firma ara..."}
      renderOption={renderTagged}
      renderValue={renderTagged}
    />
  );
}

export default memo(ParentFirmPicker);
